import type { RadioScene, SceneState } from '../scene/RadioScene';
import type { Catalog, ArtistLinks } from '../data/catalog';
import type { AudioEngine } from '../audio/AudioEngine';
import { Social, fmtClock, fmtTime } from './social';
import { dict, type Lang } from './i18n';

const PURPLE = '#685AE9';
const MUTE = '#8A8A8A';

type View = '3d' | 'list';

/** SVG-иконки (порт 1:1 из концепта). */
const ICON = {
  play: '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M7 5.5v13l11-6.5z" fill="currentColor"></path></svg>',
  pause:
    '<svg viewBox="0 0 24 24" width="18" height="18"><rect x="6" y="5" width="4" height="14" rx="1.3" fill="currentColor"></rect><rect x="14" y="5" width="4" height="14" rx="1.3" fill="currentColor"></rect></svg>',
  prev: '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M18 6v12l-8.5-6z" fill="currentColor"></path><rect x="6" y="6" width="2.4" height="12" rx="1" fill="currentColor"></rect></svg>',
  next: '<svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 6v12l8.5-6z" fill="currentColor"></path><rect x="15.6" y="6" width="2.4" height="12" rx="1" fill="currentColor"></rect></svg>',
  shuffle:
    '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 3h5v5"></path><path d="M4 20 21 3"></path><path d="M21 16v5h-5"></path><path d="M15 15l6 6"></path><path d="M4 4l5 5"></path></svg>',
  heart:
    '<svg viewBox="0 0 24 24" width="21" height="21"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="currentColor"></path></svg>',
  fire: '<svg viewBox="0 0 24 24" width="15" height="15"><path d="M12 2c.5 2.5-1.5 3.8-1.5 5.8 0 1 .7 1.8 1.6 1.9.8 0 1.2-.6 1.3-1.2 1.4 1.2 2.1 2.9 2.1 4.6a5.5 5.5 0 11-11 0c0-2.4 1.4-4.2 2.7-5.7C8.6 9.6 9.4 11 10 11.6 9.4 8 10.4 4.6 12 2z" fill="currentColor"></path></svg>',
  star: '<svg viewBox="0 0 24 24" width="15" height="15"><path d="M12 2.5l2.9 6 6.6.6-5 4.4 1.5 6.4L12 17.9 6 19.9l1.5-6.4-5-4.4 6.6-.6z" fill="currentColor"></path></svg>',
  chat: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.4 8.4 0 01-8.5 8.5 8.5 8.5 0 01-3.9-.9L3 21l1.9-5.6A8.5 8.5 0 1121 11.5z"></path></svg>',
  send: '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 2L11 13"></path><path d="M22 2l-7 20-4-9-9-4 20-7z"></path></svg>',
};

/** Иконки текущей погоды для кнопки в шапке. */
const WICON: Record<string, string> = {
  sun: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"></path></svg>',
  storm:
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 16a4 4 0 1 1 .9-7.9A5 5 0 0 1 18 9a3.5 3.5 0 0 1-.4 7"></path><path d="M12 11l-2 4h3l-2 4" stroke="#f5a623"></path></svg>',
  wind: '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 8h10a2.5 2.5 0 1 0-2.5-2.5M3 12h14a2.5 2.5 0 1 1-2.5 2.5M3 16h8"></path></svg>',
  night: '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"></path></svg>',
  acid: '<svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 2c.5 2.5-1.5 3.8-1.5 5.8 0 1 .7 1.8 1.6 1.9.8 0 1.2-.6 1.3-1.2 1.4 1.2 2.1 2.9 2.1 4.6a5.5 5.5 0 11-11 0c0-2.4 1.4-4.2 2.7-5.7C8.6 9.6 9.4 11 10 11.6 9.4 8 10.4 4.6 12 2z"></path></svg>',
};

/** Бренд-иконки соцсетей артиста (монохром, currentColor = бренд-цвет). */
const SOCIAL: Record<keyof ArtistLinks, { color: string; label: string; svg: string }> = {
  instagram: {
    color: '#E1306C',
    label: 'Instagram',
    svg: '<rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="4" fill="none" stroke="currentColor" stroke-width="2"/><circle cx="17.2" cy="6.8" r="1.3" fill="currentColor"/>',
  },
  soundcloud: {
    color: '#FF5500',
    label: 'SoundCloud',
    svg: '<path d="M10 17.5h7.2a3 3 0 0 0 .3-6 4.6 4.6 0 0 0-8.8-1.6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.6 12.5v5M6 10.8v6.7M8.2 12v5.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>',
  },
  spotify: {
    color: '#1DB954',
    label: 'Spotify',
    svg: '<circle cx="12" cy="12" r="9.3" fill="currentColor"/><path d="M7 9.8c3.4-.9 6.9-.6 9.6 1.1M7.5 12.9c2.7-.7 5.5-.4 7.7 1M7.9 15.8c2-.5 4-.3 5.6.7" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round"/>',
  },
  youtube: {
    color: '#FF0000',
    label: 'YouTube',
    svg: '<rect x="2.3" y="5.6" width="19.4" height="12.8" rx="3.6" fill="currentColor"/><path d="M10 9.4l5.4 2.6L10 14.6z" fill="#fff"/>',
  },
  bandcamp: {
    color: '#629AA9',
    label: 'Bandcamp',
    svg: '<path d="M3 9h18l-5 6H8z" fill="currentColor"/>',
  },
  tiktok: {
    color: '#111111',
    label: 'TikTok',
    svg: '<path d="M13.2 4v9.6a2.9 2.9 0 1 1-2.3-2.85V8.5A5.8 5.8 0 1 0 15.5 14V9.3a6.2 6.2 0 0 0 3.6 1.15V7.6A3.4 3.4 0 0 1 15.7 4z" fill="currentColor"/>',
  },
  facebook: {
    color: '#1877F2',
    label: 'Facebook',
    svg: '<path d="M13.5 21v-7h2.3l.45-2.95H13.5V9.1c0-.85.3-1.43 1.5-1.43h1.35V5.05A19 19 0 0 0 14.5 4.95c-2.05 0-3.45 1.25-3.45 3.55v2.55H8.6V14h2.45v7z" fill="currentColor"/>',
  },
  x: {
    color: '#111111',
    label: 'X',
    svg: '<path d="M4 4l6.6 8.6L4.3 20H6l5.5-6.4L16 20h4l-6.9-9 5.9-7H17l-5 5.8L8 4z" fill="currentColor"/>',
  },
  telegram: {
    color: '#229ED9',
    label: 'Telegram',
    svg: '<path d="M21 4.6L2.9 11.4c-.85.32-.83 1.5.03 1.78l4.4 1.4 1.7 5.1c.22.66 1.07.85 1.56.32l2.4-2.55 4.4 3.25c.6.44 1.46.12 1.63-.6L22.1 5.7c.18-.8-.55-1.42-1.1-1.1z" fill="currentColor"/><path d="M7.6 14.4l8.2-5.6-6.2 6.6z" fill="#fff" opacity="0.5"/>',
  },
  website: {
    color: '#685AE9',
    label: 'Website',
    svg: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M3 12h18M12 3c2.6 2.6 2.6 15.4 0 18M12 3c-2.6 2.6-2.6 15.4 0 18" fill="none" stroke="currentColor" stroke-width="1.6"/>',
  },
};
const SOCIAL_ORDER: (keyof ArtistLinks)[] = [
  'instagram', 'soundcloud', 'spotify', 'youtube', 'bandcamp', 'tiktok', 'facebook', 'x', 'telegram', 'website',
];

/** Ряд кликабельных иконок соцсетей артиста (только присутствующие ссылки). */
function socialRow(links: ArtistLinks | undefined, px: number): string {
  if (!links) return '';
  const sv = Math.round(px * 0.62);
  const rad = Math.max(5, Math.round(px * 0.3));
  const out: string[] = [];
  for (const k of SOCIAL_ORDER) {
    const url = links[k];
    if (!url) continue;
    const m = SOCIAL[k];
    out.push(
      `<a href="${url}" target="_blank" rel="noopener" title="${m.label}" aria-label="${m.label}" style="display:inline-flex; align-items:center; justify-content:center; width:${px}px; height:${px}px; border-radius:${rad}px; background:#f3f3f7; color:${m.color}; text-decoration:none; flex:none;"><svg viewBox="0 0 24 24" width="${sv}" height="${sv}">${m.svg}</svg></a>`,
    );
  }
  return out.join('');
}

/** Полный UI концепта: шапка, карточка now-playing, список, комменты, реакции, i18n. */
export class PlayerUI {
  private root: HTMLDivElement;
  private lang: Lang;
  private view: View;
  private shuffle: boolean;
  private trackId = 0;
  private absSlot = -1;
  private isLive = true;
  private albumPlatform = -1; // платформа открытого в AlbumView трека
  private liveSlot = 0;
  private liveStartMs = 0;
  private durSec = 0;
  private playing = true;
  private commentsOpen = false;
  private unlocked = false; // включён ли звук после первого жеста
  private collapsed = false; // свёрнут ли плеер в 3D (любоваться сценой)
  private dragPos = { x: 0, y: 0 }; // смещение плеера (перетаскивание), localStorage
  private titleCardTimer: ReturnType<typeof setTimeout> | null = null; // тайтл-карточка смены трека
  private tcInit = false; // первый emit пропускаем (не мигать карточкой на загрузке)
  // дев-панель регуляторов (localStorage 'grv-pfm-dev')
  private dev = { beat: 0.6, particles: 1, flyby: 1, covers: true, light: 1, bloom: 1, sat: 1 };
  private devOpen = false;

  constructor(
    private scene: RadioScene,
    private catalog: Catalog,
    private audio: AudioEngine,
    private social: Social,
  ) {
    const pref = this.loadPrefs();
    this.lang = pref.lang;
    this.view = pref.view;
    this.shuffle = pref.shuffle;

    this.root = document.createElement('div');
    this.root.style.cssText = 'position:fixed; inset:0; z-index:5; pointer-events:none; font-family:var(--font-body);';
    this.root.innerHTML = this.markup();
    document.body.appendChild(this.root);

    this.wire();
    this.loadDev();
    this.wireDev();
    this.applyDev();
    scene.onState((s) => this.onScene(s));
    audio.onTime(() => this.onTime());
    audio.onEnded(() => this.next());
    this.social.onChange(() => this.render()); // соц-данные с бэкенда → перерисовка
    // первый жест любого типа — включить звук, FFT и ресинхрон под эфир
    // (до жеста эфир уже идёт muted-автоплеем, см. startPlayback)
    const evs = ['pointerdown', 'touchstart', 'keydown', 'click'];
    const unlock = (): void => {
      if (this.unlocked) return;
      this.unlocked = true;
      this.audio.unmute();
      this.audio.connectAnalyser(); // Web Audio FFT — для beat-анимации
      if (this.isLive) {
        try {
          this.audio.el.currentTime = Math.max(0, (Date.now() - this.liveStartMs) / 1000);
        } catch {
          /* noop */
        }
      }
      if (this.playing) void this.audio.play();
      for (const ev of evs) removeEventListener(ev, unlock);
    };
    for (const ev of evs) addEventListener(ev, unlock, { passive: true });

    this.render();
  }

  /** Старт воспроизведения: до первого жеста — muted-автоплей, после — обычный. */
  private startPlayback(): void {
    if (this.unlocked) void this.audio.play();
    else void this.audio.autostart();
  }

  // ---------- prefs ----------
  private loadPrefs(): { lang: Lang; shuffle: boolean; view: View } {
    try {
      const o = JSON.parse(localStorage.getItem('grv-pfm-prefs') || '{}');
      this.collapsed = !!o.collapsed;
      const p = JSON.parse(localStorage.getItem('grv-pfm-pos') || 'null');
      if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) this.dragPos = { x: p.x, y: p.y };
      return { lang: o.lang === 'en' ? 'en' : 'ru', shuffle: !!o.shuffle, view: o.view === 'list' ? 'list' : '3d' };
    } catch {
      return { lang: 'ru', shuffle: false, view: '3d' };
    }
  }
  private savePrefs(): void {
    try {
      localStorage.setItem('grv-pfm-prefs', JSON.stringify({ lang: this.lang, shuffle: this.shuffle, view: this.view, collapsed: this.collapsed }));
    } catch {
      /* noop */
    }
  }

  // ---------- refs ----------
  private q<T extends HTMLElement>(ref: string): T {
    return this.root.querySelector(`[data-ref="${ref}"]`) as T;
  }

  // ---------- scene/audio sync ----------
  private onScene(s: SceneState): void {
    const changed = s.absSlot !== this.absSlot;
    this.absSlot = s.absSlot;
    this.trackId = s.trackId;
    this.isLive = s.isLive;
    this.liveSlot = s.liveSlot;
    this.liveStartMs = s.liveStartMs;
    this.playing = s.playing;
    this.durSec = s.durSec;
    if (changed) {
      const tr = this.catalog.tracks[this.trackId];
      void this.social.load(this.trackId); // подтянуть комменты/реакции трека с бэкенда
      this.audio.load(tr.audioUrl);
      // синхрон эфира: на live — seek в текущий offset; в прошлом — с начала
      const seek = this.isLive ? Math.max(0, (Date.now() - this.liveStartMs) / 1000) : 0;
      const apply = (): void => {
        try {
          this.audio.el.currentTime = seek;
        } catch {
          /* noop */
        }
      };
      if (this.audio.el.readyState >= 1) apply();
      else this.audio.el.addEventListener('loadedmetadata', apply, { once: true });
      if (this.playing) this.startPlayback();
      // крупная карточка «артист + трек» на смене трека (первый emit — пропускаем)
      if (this.tcInit) this.showTitleCard(tr);
      else this.tcInit = true;
    } else if (this.playing) this.startPlayback();
    else this.audio.pause();
    this.render();
  }

  /** Крупная карточка «АРТИСТ + название» при смене трека: появляется и через ~5с тает. */
  private showTitleCard(tr: { artist: string; title: string }): void {
    const card = this.q<HTMLElement>('titleCard');
    if (!card || !tr) return;
    this.q<HTMLElement>('tcArtist').textContent = tr.artist;
    this.q<HTMLElement>('tcTitle').textContent = tr.title;
    card.style.opacity = '1';
    card.style.transform = 'translateY(0)';
    if (this.titleCardTimer) clearTimeout(this.titleCardTimer);
    this.titleCardTimer = setTimeout(() => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(16px)';
    }, 5000);
  }

  // ---------- дев-панель регуляторов ----------
  private loadDev(): void {
    try {
      const o = JSON.parse(localStorage.getItem('grv-pfm-dev') || '{}') as Record<string, unknown>;
      const num = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
      this.dev = {
        beat: num(o.beat, 0.6),
        particles: num(o.particles, 1),
        flyby: num(o.flyby, 1),
        covers: o.covers !== false,
        light: num(o.light, 1),
        bloom: num(o.bloom, 1),
        sat: num(o.sat, 1),
      };
      this.devOpen = o.open === true;
    } catch {
      /* noop */
    }
  }
  private saveDev(): void {
    try {
      localStorage.setItem('grv-pfm-dev', JSON.stringify({ ...this.dev, open: this.devOpen }));
    } catch {
      /* noop */
    }
  }
  private updateDevLabels(): void {
    const t = dict(this.lang, this.scene.count);
    const lab = (ref: string, name: string, v: number, suffix = '×'): void => {
      const el = this.q<HTMLElement>(ref);
      if (el) el.innerHTML = `<span>${name}</span><span style="color:#262626;font-weight:700;">${v.toFixed(2)}${suffix}</span>`;
    };
    lab('lblBeat', t.dvBeat, this.dev.beat, '');
    lab('lblParticles', t.dvParticles, this.dev.particles);
    lab('lblFlyby', t.dvFlyby, this.dev.flyby);
    lab('lblLight', t.dvLight, this.dev.light);
    lab('lblBloom', t.dvBloom, this.dev.bloom);
    lab('lblSat', t.dvSat, this.dev.sat);
    const lc = this.q<HTMLElement>('lblCovers');
    if (lc) lc.textContent = t.dvCovers;
    const dt = this.q<HTMLElement>('devTitle');
    if (dt) dt.textContent = t.devTitle;
  }
  /** Применить значения дев-панели к сцене/аудио и проставить контролы. */
  private applyDev(): void {
    const setRange = (ref: string, v: number): void => {
      const el = this.q<HTMLInputElement>(ref);
      if (el) el.value = String(v);
    };
    setRange('rngBeat', this.dev.beat);
    setRange('rngParticles', this.dev.particles);
    setRange('rngFlyby', this.dev.flyby);
    setRange('rngLight', this.dev.light);
    setRange('rngBloom', this.dev.bloom);
    setRange('rngSat', this.dev.sat);
    const chk = this.q<HTMLInputElement>('chkCovers');
    if (chk) chk.checked = this.dev.covers;
    this.audio.setBeatSensitivity(this.dev.beat);
    this.scene.setParticleDensity(this.dev.particles);
    this.scene.setFlybyFrequency(this.dev.flyby);
    this.scene.setCoversEnabled(this.dev.covers);
    this.scene.setLightGain(this.dev.light);
    this.scene.setBloom(this.dev.bloom);
    this.scene.setSaturation(this.dev.sat);
    this.q<HTMLElement>('devPanel').style.display = this.devOpen ? 'block' : 'none';
    this.updateDevLabels();
  }
  private wireDev(): void {
    const bind = (ref: string, set: (v: number) => void, apply: (v: number) => void): void => {
      const el = this.q<HTMLInputElement>(ref);
      if (!el) return;
      el.addEventListener('input', () => {
        const v = parseFloat(el.value);
        set(v);
        apply(v);
        this.updateDevLabels();
        this.saveDev();
      });
    };
    bind('rngBeat', (v) => (this.dev.beat = v), (v) => this.audio.setBeatSensitivity(v));
    bind('rngParticles', (v) => (this.dev.particles = v), (v) => this.scene.setParticleDensity(v));
    bind('rngFlyby', (v) => (this.dev.flyby = v), (v) => this.scene.setFlybyFrequency(v));
    bind('rngLight', (v) => (this.dev.light = v), (v) => this.scene.setLightGain(v));
    bind('rngBloom', (v) => (this.dev.bloom = v), (v) => this.scene.setBloom(v));
    bind('rngSat', (v) => (this.dev.sat = v), (v) => this.scene.setSaturation(v));
    const chk = this.q<HTMLInputElement>('chkCovers');
    if (chk)
      chk.addEventListener('change', () => {
        this.dev.covers = chk.checked;
        this.scene.setCoversEnabled(chk.checked);
        this.saveDev();
      });
    const tog = this.q<HTMLElement>('devToggle');
    if (tog)
      tog.addEventListener('click', () => {
        this.devOpen = !this.devOpen;
        this.q<HTMLElement>('devPanel').style.display = this.devOpen ? 'block' : 'none';
        this.saveDev();
      });
  }

  private onTime(): void {
    const d = this.audio.duration || this.durSec || 1;
    const pct = Math.min(100, (this.audio.time / d) * 100);
    const p = this.q<HTMLElement>('progress');
    if (p) p.style.width = pct.toFixed(2) + '%';
    const tl = this.q<HTMLElement>('time');
    if (tl) tl.textContent = fmtTime(this.audio.time);
  }

  private next(): void {
    this.scene.hopStep(1); // вверх — к эфиру (выше live нельзя, clamp в сцене)
  }
  private prev(): void {
    this.scene.hopStep(-1); // вниз — в прошлое
  }

  // ---------- wire ----------
  private wire(): void {
    const on = (ref: string, ev: string, fn: () => void): void => {
      const el = this.q<HTMLElement>(ref);
      if (el) el.addEventListener(ev, fn);
    };
    on('tab3d', 'click', () => this.setView('3d'));
    on('tabList', 'click', () => this.setView('list'));
    on('ru', 'click', () => this.setLang('ru'));
    on('en', 'click', () => this.setLang('en'));
    on('play', 'click', () => this.scene.togglePlay());
    on('prev', 'click', () => this.prev());
    on('next', 'click', () => this.next());
    on('like', 'click', () => this.toggleLike());
    on('shuffle', 'click', () => this.scene.goLive()); // кнопка слева = «в эфир»
    on('status', 'click', () => {
      if (!this.isLive) this.scene.goLive();
    });
    on('fire', 'click', () => {
      this.social.addReact(this.trackId, 'fire');
      this.render();
    });
    on('star', 'click', () => {
      this.social.addReact(this.trackId, 'star');
      this.render();
    });
    on('comments', 'click', () => {
      this.commentsOpen = !this.commentsOpen;
      this.render();
    });
    on('send', 'click', () => this.sendComment());
    on('weather', 'click', () => {
      this.scene.cycleWeather();
      this.render();
    });
    // верхняя «пилюля» = хват: перетащить плеер по экрану; короткий тап — свернуть
    const handle = this.q<HTMLElement>('collapse');
    const wrap = this.q<HTMLElement>('playerWrap');
    const applyPos = (): void => {
      wrap.style.transform = `translate(${this.dragPos.x}px, ${this.dragPos.y}px)`;
    };
    const clampPos = (): void => {
      const r = wrap.getBoundingClientRect();
      if (!r.width) return;
      const maxX = Math.max(0, (window.innerWidth - r.width) / 2 - 8);
      const minY = -Math.max(0, window.innerHeight - r.height - 64);
      this.dragPos.x = Math.max(-maxX, Math.min(maxX, this.dragPos.x));
      this.dragPos.y = Math.max(minY, Math.min(12, this.dragPos.y));
    };
    applyPos();
    let sx = 0,
      sy = 0,
      ox = 0,
      oy = 0,
      dragId = -1;
    handle.addEventListener('pointerdown', (e) => {
      dragId = e.pointerId;
      sx = e.clientX;
      sy = e.clientY;
      ox = this.dragPos.x;
      oy = this.dragPos.y;
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    });
    handle.addEventListener('pointermove', (e) => {
      if (e.pointerId !== dragId) return;
      this.dragPos.x = ox + (e.clientX - sx);
      this.dragPos.y = oy + (e.clientY - sy);
      clampPos();
      applyPos();
    });
    handle.addEventListener('pointerup', (e) => {
      if (e.pointerId !== dragId) return;
      dragId = -1;
      const moved = Math.abs(e.clientX - sx) + Math.abs(e.clientY - sy);
      if (moved < 6) {
        this.collapsed = true; // короткий тап — свернуть
        this.savePrefs();
        this.render();
      } else {
        try {
          localStorage.setItem('grv-pfm-pos', JSON.stringify(this.dragPos));
        } catch {
          /* noop */
        }
      }
    });
    addEventListener('resize', () => {
      clampPos();
      applyPos();
    });
    on('expand', 'click', () => {
      this.collapsed = false;
      this.savePrefs();
      this.render();
    });
    on('albumClose', 'click', () => this.closeAlbum());
    on('albumPlay', 'click', () => {
      this.scene.jumpTo(this.albumPlatform);
      this.closeAlbum();
      this.setView('3d');
    });
    const input = this.q<HTMLInputElement>('cinput');
    if (input)
      input.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter') this.sendComment();
      });
  }

  private setView(v: View): void {
    this.view = v;
    this.savePrefs();
    this.render();
  }
  private setLang(l: Lang): void {
    this.lang = l;
    this.savePrefs();
    this.render();
    this.updateDevLabels();
  }
  private toggleLike(): void {
    const liked = this.social.toggleLike(this.trackId);
    if (liked) this.spawnHearts();
    this.render();
  }
  private sendComment(): void {
    const input = this.q<HTMLInputElement>('cinput');
    if (!input) return;
    const txt = (input.value || '').trim();
    if (!txt) return;
    void this.social.addComment(this.trackId, txt, Math.floor(this.audio.time * 1000));
    input.value = '';
    this.render();
  }
  private spawnHearts(): void {
    const host = this.q<HTMLElement>('burst');
    if (!host) return;
    for (let i = 0; i < 6; i++) {
      const s = document.createElement('span');
      s.innerHTML =
        '<svg viewBox="0 0 24 24" width="13" height="13"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#e8557a"/></svg>';
      s.style.cssText =
        'position:absolute; left:' +
        (Math.random() * 26 - 13) +
        'px; bottom:0; pointer-events:none; animation:grv-heart-rise ' +
        (0.8 + Math.random() * 0.5) +
        's ease-out forwards; animation-delay:' +
        i * 0.05 +
        's;';
      host.appendChild(s);
      setTimeout(() => s.remove(), 1500);
    }
  }

  // ---------- render ----------
  private render(): void {
    const t = dict(this.lang, this.scene.count);
    const tr = this.catalog.tracks[this.trackId];
    const behind = this.liveSlot - this.absSlot; // на сколько треков назад от эфира
    const liked = this.social.isLiked(this.trackId);
    const r = this.social.reactOf(this.trackId);
    const cms = this.social.cmtsOf(this.trackId);

    // header
    this.q('brand').textContent = t.brand;
    this.q('tagline').textContent = t.tag;
    this.styleTab('tab3d', this.view === '3d');
    this.styleTab('tabList', this.view === 'list');
    this.q('tab3d').textContent = t.m3d;
    this.q('tabList').textContent = t.mList;
    this.q('weather').innerHTML =
      `<span style="display:inline-flex; align-items:center; justify-content:center; width:23px; height:23px; border-radius:7px; background:${PURPLE}; color:#fff;">${WICON[this.scene.weatherKey()] || WICON.sun}</span>` +
      `<span>${t.weather}</span>`;
    this.styleTab('ru', this.lang === 'ru');
    this.styleTab('en', this.lang === 'en');
    this.q('hint').textContent = t.hint;
    this.q<HTMLElement>('hintWrap').style.display = this.view === '3d' ? '' : 'none';

    // card content
    if (tr.coverUrl) this.q<HTMLImageElement>('cover').src = tr.coverUrl;
    const status = this.q<HTMLElement>('status');
    if (this.isLive) {
      status.textContent = '● ' + (this.lang === 'en' ? 'LIVE' : 'В ЭФИРЕ');
      status.style.color = '#e8557a';
      status.style.cursor = 'default';
    } else {
      status.textContent = '⏮ −' + behind + ' · ' + (this.lang === 'en' ? 'back to live' : 'в эфир');
      status.style.color = PURPLE;
      status.style.cursor = 'pointer';
    }
    this.q('title').textContent = tr.title;
    this.q('artist').textContent = tr.artist;
    this.q('artistSocial').innerHTML = socialRow(this.catalog.artistLinks(tr.artist), 20);
    this.q('dur').textContent = fmtTime(this.durSec);
    this.q('play').innerHTML = this.playing ? ICON.pause : ICON.play;
    const likeBtn = this.q<HTMLElement>('like');
    likeBtn.style.background = liked ? 'rgba(232,85,122,0.12)' : '#f3f3f7';
    likeBtn.style.color = liked ? '#e8557a' : '#a4a8b8';
    this.q('fireN').textContent = String(r.fire);
    this.q('starN').textContent = String(r.star);
    this.q('commentN').textContent = String(cms.length);
    // левая кнопка = «в эфир» (подсвечена, когда мы в прошлом)
    const live = this.q<HTMLElement>('shuffle');
    live.style.background = this.isLive ? '#f3f3f7' : 'rgba(104,90,233,0.14)';
    live.style.color = this.isLive ? '#cdd0db' : PURPLE;
    live.style.cursor = this.isLive ? 'default' : 'pointer';

    // промо-ссылки на площадки (Apple Music и др.)
    const linksEl = this.q<HTMLElement>('links');
    const lk = tr.links || {};
    const chip = (label: string, url: string, color: string): string =>
      `<a href="${url}" target="_blank" rel="noopener" style="display:inline-flex; align-items:center; gap:5px; height:26px; padding:0 11px; border-radius:999px; background:#f5f5f8; color:#262626; font-size:11px; font-weight:600; text-decoration:none;"><span style="width:7px; height:7px; border-radius:50%; background:${color};"></span>${label}</a>`;
    const chips: string[] = [];
    if (lk.appleMusic) chips.push(chip('Apple Music', lk.appleMusic, '#fa2d48'));
    if (lk.spotify) chips.push(chip('Spotify', lk.spotify, '#1db954'));
    if (lk.youtube) chips.push(chip('YouTube', lk.youtube, '#ff0000'));
    linksEl.innerHTML = chips.join('');
    linksEl.style.display = chips.length ? 'flex' : 'none';

    // comments panel
    const panel = this.q<HTMLElement>('cpanel');
    panel.style.display = this.commentsOpen ? '' : 'none';
    this.q<HTMLElement>('card').style.borderRadius = this.commentsOpen ? '0 0 15px 15px' : '15px';
    if (this.commentsOpen) {
      this.q('cpanelTitle').textContent = t.comments + ' · ' + cms.length;
      const list = this.q<HTMLElement>('clist');
      list.innerHTML =
        cms.length === 0
          ? `<div style="font-size:12.5px; color:#a4a8b8; padding:4px 0;">${t.noC}</div>`
          : cms
              .slice()
              .reverse()
              .map(
                (c) =>
                  `<div style="display:flex; gap:9px; align-items:flex-start;"><span style="flex:none; width:26px; height:26px; border-radius:50%; background:#ece9fb; color:${PURPLE}; font-size:11px; font-weight:700; text-transform:uppercase; display:flex; align-items:center; justify-content:center;">${esc((c.author || t.you).slice(0, 1))}</span><div style="flex:1; min-width:0;"><div style="font-size:13px; color:#262626; line-height:1.4; word-break:break-word;">${esc(c.text)}</div><div style="font-size:10px; color:#a4a8b8; margin-top:2px;">${esc(c.author || t.you)}${c.offsetMs ? ' · ' + fmtTime(c.offsetMs / 1000) : ''} · ${fmtClock(c.t)}</div></div></div>`,
              )
              .join('');
      this.q<HTMLInputElement>('cinput').placeholder = t.write;
    }

    // плеер виден в ОБОИХ режимах (в списке — плавает поверх, как now-playing бар),
    // чтобы не терять стриминг-ссылки и транспорт; свернуть можно в любом режиме
    this.q<HTMLElement>('playerWrap').style.display = !this.collapsed ? '' : 'none';
    this.q<HTMLElement>('expandWrap').style.display = this.collapsed ? '' : 'none';
    this.q('expandLabel').textContent = this.lang === 'en' ? 'player' : 'плеер';
    const listWrap = this.q<HTMLElement>('listWrap');
    listWrap.style.display = this.view === 'list' ? '' : 'none';
    if (this.view === 'list') this.renderList(t.allTracks);
  }

  private styleTab(ref: string, active: boolean): void {
    const el = this.q<HTMLElement>(ref);
    el.style.background = active ? PURPLE : 'transparent';
    el.style.color = active ? '#ffffff' : MUTE;
  }

  private renderList(allTracksLabel: string): void {
    const tr = this.catalog.tracks[this.trackId];
    if (tr.coverUrl) this.q<HTMLImageElement>('listCover').src = tr.coverUrl;
    this.q('listTitle').textContent = tr.title;
    this.q('listArtist').textContent = tr.artist;
    this.q('listArtistSocial').innerHTML = socialRow(this.catalog.artistLinks(tr.artist), 24);

    const slots = this.scene.windowSlots(); // live первым, затем прошлое
    const liveP = slots.length ? slots[0].platform : 0;
    const curP = this.scene.index;
    this.q('listLabel').textContent = allTracksLabel + ' · ' + slots.length;

    const rows = this.q<HTMLElement>('listRows');
    rows.innerHTML = '';
    for (const w of slots) {
      const t2 = this.catalog.tracks[w.trackId];
      const isCur = w.platform === curP;
      const tag = w.isLive ? (this.lang === 'en' ? 'LIVE' : 'ЭФИР') : '−' + (liveP - w.platform);
      const tagColor = w.isLive ? '#e8557a' : isCur ? PURPLE : '#a4a8b8';
      const row = document.createElement('div');
      row.style.cssText = `display:flex; align-items:center; gap:14px; padding:9px 10px; border-radius:12px; cursor:pointer; background:${isCur ? '#ffffff' : 'transparent'};`;
      row.innerHTML =
        `<span style="flex:none; width:38px; text-align:center; font-size:10px; font-weight:700; letter-spacing:0.04em; font-variant-numeric:tabular-nums; color:${tagColor};">${tag}</span>` +
        `<img alt="" src="${t2.coverUrl || ''}" style="flex:none; width:46px; height:46px; border-radius:8px; object-fit:cover; background:#e6e8f1;">` +
        `<div style="flex:1; min-width:0;"><div style="font-size:14.5px; font-weight:600; color:${isCur ? PURPLE : '#262626'}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(t2.title)}</div><div style="font-size:12.5px; color:#9498a8; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(t2.artist)}</div></div>` +
        `<span style="flex:none; font-size:12px; color:#a4a8b8; font-variant-numeric:tabular-nums;">${fmtTime(t2.durationMs / 1000)}</span>`;
      row.addEventListener('click', () => this.openAlbum(w));
      rows.appendChild(row);
    }
  }

  // ---------- полноэкранный альбом ----------
  private openAlbum(w: { platform: number; trackId: number; isLive: boolean }): void {
    this.albumPlatform = w.platform;
    const tr = this.catalog.tracks[w.trackId];
    const en = this.lang === 'en';
    if (tr.coverUrl) this.q<HTMLImageElement>('albumCover').src = tr.coverUrl;
    this.q('albumTitle').textContent = tr.title;
    this.q('albumArtist').textContent = tr.artist;
    this.q('albumArtistSocial').innerHTML = socialRow(this.catalog.artistLinks(tr.artist), 32);
    this.q('albumAlbum').textContent = tr.album || '';
    const liveP = this.scene.windowSlots()[0]?.platform ?? w.platform;
    this.q('albumTag').textContent = w.isLive
      ? en
        ? '● ON AIR'
        : '● В ЭФИРЕ'
      : en
        ? `${liveP - w.platform} tracks back`
        : `${liveP - w.platform} треков назад`;
    this.q('albumPlayLabel').textContent = en ? 'listen' : 'слушать';
    this.q('albumLinksLabel').textContent = en ? 'listen on' : 'слушать на площадках';
    const big = (label: string, url: string, color: string): string =>
      `<a href="${url}" target="_blank" rel="noopener" style="display:flex; align-items:center; gap:12px; height:52px; padding:0 18px; border-radius:14px; background:#fff; color:#262626; text-decoration:none; font-size:15px; font-weight:600; box-shadow:0 2px 10px rgba(38,38,40,0.07);"><span style="width:30px; height:30px; border-radius:8px; background:${color};"></span>${label}<span style="margin-left:auto; color:#c0c3d0;">→</span></a>`;
    const lk = tr.links || {};
    const chips: string[] = [];
    if (lk.appleMusic) chips.push(big('Apple Music', lk.appleMusic, '#fa2d48'));
    if (lk.spotify) chips.push(big('Spotify', lk.spotify, '#1db954'));
    if (lk.youtube) chips.push(big('YouTube', lk.youtube, '#ff0000'));
    this.q<HTMLElement>('albumLinks').innerHTML =
      chips.join('') || `<div style="color:#a4a8b8; font-size:13px;">${en ? 'no links yet' : 'ссылок пока нет'}</div>`;
    this.q<HTMLElement>('albumWrap').style.display = 'block';
  }
  private closeAlbum(): void {
    this.q<HTMLElement>('albumWrap').style.display = 'none';
  }

  // ---------- markup ----------
  private markup(): string {
    return `
<div style="position:absolute; z-index:9; top:0; left:0; right:0; display:flex; align-items:center; justify-content:space-between; gap:10px 14px; flex-wrap:wrap; padding:18px 20px; pointer-events:none;">
  <div style="display:flex; align-items:center; gap:9px; background:#fff; border-radius:14px; padding:8px 14px; box-shadow:0 2px 12px rgba(38,38,40,0.12); pointer-events:auto;">
    <span style="flex:none; width:9px; height:9px; border-radius:50%; background:${PURPLE}; box-shadow:0 0 0 4px rgba(104,90,233,0.16);"></span>
    <div>
      <div data-ref="brand" style="font-family:var(--font-display); font-weight:900; font-size:19px; line-height:1; letter-spacing:-0.02em; color:#262626;"></div>
      <div data-ref="tagline" style="margin-top:3px; font-size:9.5px; letter-spacing:0.14em; text-transform:uppercase; font-weight:600; color:${MUTE};"></div>
    </div>
  </div>
  <div style="display:flex; gap:7px; align-items:center; pointer-events:auto;">
    <button data-ref="weather" title="погода" style="display:inline-flex; align-items:center; gap:6px; border:none; cursor:pointer; border-radius:10px; padding:4px 12px 4px 5px; font-size:11px; font-weight:700; letter-spacing:0.03em; background:#fff; color:#685AE9; box-shadow:0 2px 12px rgba(38,38,40,0.12);"></button>
    <div style="display:flex; gap:2px; background:#fff; border-radius:10px; padding:3px; box-shadow:0 2px 12px rgba(38,38,40,0.12);">
      <button data-ref="tab3d" style="border:none; cursor:pointer; border-radius:7px; padding:6px 11px; font-size:11px; font-weight:700; letter-spacing:0.04em;"></button>
      <button data-ref="tabList" style="border:none; cursor:pointer; border-radius:7px; padding:6px 11px; font-size:11px; font-weight:700; letter-spacing:0.04em;"></button>
    </div>
    <div style="display:flex; gap:2px; background:#fff; border-radius:10px; padding:3px; box-shadow:0 2px 12px rgba(38,38,40,0.12);">
      <button data-ref="ru" style="border:none; cursor:pointer; border-radius:7px; padding:6px 10px; font-size:11px; font-weight:700; letter-spacing:0.05em;">RU</button>
      <button data-ref="en" style="border:none; cursor:pointer; border-radius:7px; padding:6px 10px; font-size:11px; font-weight:700; letter-spacing:0.05em;">EN</button>
    </div>
    <button data-ref="devToggle" title="dev" style="border:none; cursor:pointer; border-radius:10px; width:32px; height:30px; font-size:15px; line-height:1; background:#fff; color:#685AE9; box-shadow:0 2px 12px rgba(38,38,40,0.12);">⚙</button>
  </div>
</div>

<div data-ref="devPanel" style="display:none; position:absolute; z-index:9; top:64px; right:20px; width:250px; max-height:78vh; overflow-y:auto; background:#fff; border-radius:14px; padding:14px 16px; box-shadow:0 10px 34px rgba(38,38,40,0.2); pointer-events:auto;">
  <div data-ref="devTitle" style="font-weight:800; letter-spacing:0.05em; text-transform:uppercase; color:#262626; font-size:10.5px; margin-bottom:12px;"></div>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblBeat" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngBeat" type="range" min="0" max="1" step="0.01" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblParticles" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngParticles" type="range" min="0" max="1.5" step="0.05" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblFlyby" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngFlyby" type="range" min="0.2" max="3" step="0.1" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblLight" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngLight" type="range" min="0.2" max="2.5" step="0.05" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblBloom" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngBloom" type="range" min="0" max="3" step="0.05" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:block; margin-bottom:11px; font-size:10.5px;"><span data-ref="lblSat" style="display:flex; justify-content:space-between; color:#8A8A8A; margin-bottom:4px;"></span><input data-ref="rngSat" type="range" min="0" max="2" step="0.05" style="width:100%; accent-color:#685AE9;"></label>
  <label style="display:flex; align-items:center; justify-content:space-between; font-size:10.5px; color:#8A8A8A; cursor:pointer;"><span data-ref="lblCovers"></span><input data-ref="chkCovers" type="checkbox" style="accent-color:#685AE9; width:16px; height:16px; cursor:pointer;"></label>
</div>

<div data-ref="titleCard" style="position:absolute; z-index:8; left:0; right:0; top:29%; display:flex; justify-content:center; padding:0 24px; pointer-events:none; opacity:0; transform:translateY(16px); transition:opacity 0.5s ease, transform 0.6s cubic-bezier(0.22,1,0.36,1);">
  <div style="display:inline-flex; flex-direction:column; align-items:center; text-align:center; gap:7px; padding:18px 32px; border-radius:20px; background:rgba(18,18,28,0.42); backdrop-filter:blur(8px); -webkit-backdrop-filter:blur(8px); box-shadow:0 14px 44px rgba(20,20,30,0.3);">
    <div data-ref="tcArtist" style="font-family:var(--font-display); font-weight:900; font-size:clamp(28px,5.5vw,54px); line-height:1.02; letter-spacing:-0.02em; color:#fff; text-transform:uppercase; text-shadow:0 2px 16px rgba(0,0,0,0.45);"></div>
    <div data-ref="tcTitle" style="font-family:var(--font-display); font-weight:700; font-size:clamp(14px,2.4vw,22px); letter-spacing:0.02em; color:#E6E2FF; text-shadow:0 1px 10px rgba(0,0,0,0.5);"></div>
  </div>
</div>

<div data-ref="hintWrap" style="position:absolute; z-index:5; bottom:18px; left:50%; transform:translateX(-50%); pointer-events:none; font-size:11.5px; letter-spacing:0.03em; color:#9498a8; white-space:nowrap; max-width:94vw; overflow:hidden; text-overflow:ellipsis;"><span data-ref="hint"></span></div>

<div data-ref="playerWrap" style="position:absolute; z-index:7; left:0; right:0; margin:0 auto; bottom:max(14px, env(safe-area-inset-bottom)); width:min(460px, calc(100vw - 24px)); box-sizing:border-box; pointer-events:auto;">
  <div data-ref="collapse" title="перетащить / свернуть" style="margin:0 auto 7px; width:52px; height:22px; display:flex; align-items:center; justify-content:center; background:#fff; border-radius:999px; box-shadow:0 2px 10px rgba(38,38,40,0.12); cursor:grab; touch-action:none;">
    <svg viewBox="0 0 24 24" width="17" height="17"><path d="M6 10l6 6 6-6" fill="none" stroke="#9498a8" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"></path></svg>
  </div>
  <div data-ref="cpanel" style="display:none; background:#fff; border-radius:15px 15px 0 0; border-bottom:1px solid #EBEBEB; box-shadow:0 -8px 30px rgba(38,38,40,0.10); padding:14px 16px; display:flex; flex-direction:column; gap:11px;">
    <span data-ref="cpanelTitle" style="font-size:11px; letter-spacing:0.14em; text-transform:uppercase; font-weight:700; color:#262626;"></span>
    <div data-ref="clist" style="overflow-y:auto; display:flex; flex-direction:column; gap:11px; max-height:138px;"></div>
    <div style="display:flex; gap:8px; align-items:center;">
      <input data-ref="cinput" style="flex:1; min-width:0; border:1px solid #e3e3ea; border-radius:10px; padding:10px 12px; font-size:13px; color:#262626; outline:none; background:#fafafb;">
      <button data-ref="send" style="flex:none; width:40px; height:40px; border:none; border-radius:10px; background:${PURPLE}; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center;">${ICON.send}</button>
    </div>
  </div>

  <div data-ref="card" style="background:#fff; border-radius:15px; box-shadow:0 1px 2px rgba(0,0,0,0.04),0 18px 50px rgba(38,38,40,0.16); padding:14px;">
    <div style="display:flex; gap:14px; align-items:center;">
      <img data-ref="cover" alt="" style="width:64px; height:64px; border-radius:10px; object-fit:cover; flex:none; background:#EBEBEB; box-shadow:0 5px 14px rgba(38,38,40,0.16);">
      <div style="flex:1; min-width:0;">
        <div style="display:flex; align-items:center; gap:6px;">
          <span style="display:inline-flex; gap:2px; height:10px; align-items:flex-end;">
            <span style="width:2.5px; height:100%; background:${PURPLE}; border-radius:2px; transform-origin:bottom; animation:grv-eq 0.7s ease-in-out infinite;"></span>
            <span style="width:2.5px; height:100%; background:${PURPLE}; border-radius:2px; transform-origin:bottom; animation:grv-eq 0.7s ease-in-out infinite 0.18s;"></span>
            <span style="width:2.5px; height:100%; background:${PURPLE}; border-radius:2px; transform-origin:bottom; animation:grv-eq 0.7s ease-in-out infinite 0.36s;"></span>
          </span>
          <span data-ref="status" style="font-size:9px; letter-spacing:0.15em; text-transform:uppercase; font-weight:700; color:${PURPLE};"></span>
        </div>
        <div data-ref="title" style="margin-top:4px; font-family:var(--font-display); font-weight:900; font-size:17px; line-height:1.1; letter-spacing:-0.01em; color:#262626; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;"></div>
        <div style="display:flex; align-items:center; gap:7px; margin-top:2px; min-width:0;">
          <span data-ref="artist" style="font-size:12px; color:${MUTE}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;"></span>
          <span data-ref="artistSocial" style="display:flex; align-items:center; gap:4px; flex:none;"></span>
        </div>
      </div>
      <div style="position:relative; flex:none;">
        <button data-ref="like" style="width:42px; height:42px; border:none; border-radius:50%; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.15s;">${ICON.heart}</button>
        <span data-ref="burst" style="position:absolute; left:50%; top:4px; width:0; height:0; pointer-events:none;"></span>
      </div>
    </div>

    <div data-ref="links" style="display:none; gap:6px; flex-wrap:wrap; margin-top:11px;"></div>

    <div style="margin-top:13px;">
      <div style="position:relative; height:4px; border-radius:2px; background:#EBEBEB;">
        <span data-ref="progress" style="position:absolute; left:0; top:0; bottom:0; width:0%; background:${PURPLE}; border-radius:2px;"><span style="position:absolute; right:-5px; top:50%; transform:translateY(-50%); width:10px; height:10px; border-radius:50%; background:${PURPLE}; box-shadow:0 1px 3px rgba(38,38,40,0.3);"></span></span>
      </div>
      <div style="display:flex; justify-content:space-between; margin-top:7px; font-size:11px; color:#a4a8b8; font-variant-numeric:tabular-nums;">
        <span data-ref="time">0:00</span><span data-ref="dur"></span>
      </div>
    </div>

    <div style="margin-top:8px; display:flex; align-items:center; justify-content:space-between;">
      <div style="display:flex; align-items:center; gap:6px;">
        <button data-ref="shuffle" style="width:34px; height:34px; border:none; border-radius:9px; cursor:pointer; display:flex; align-items:center; justify-content:center;">${ICON.shuffle}</button>
        <button data-ref="prev" style="width:36px; height:36px; border:none; border-radius:9px; background:#f3f3f7; color:#262626; cursor:pointer; display:flex; align-items:center; justify-content:center;">${ICON.prev}</button>
        <button data-ref="play" style="width:46px; height:46px; border:none; border-radius:50%; background:${PURPLE}; color:#fff; cursor:pointer; display:flex; align-items:center; justify-content:center; box-shadow:0 6px 16px rgba(104,90,233,0.35);"></button>
        <button data-ref="next" style="width:36px; height:36px; border:none; border-radius:9px; background:#f3f3f7; color:#262626; cursor:pointer; display:flex; align-items:center; justify-content:center;">${ICON.next}</button>
      </div>
      <div style="display:flex; align-items:center; gap:5px;">
        <button data-ref="fire" style="display:flex; align-items:center; gap:4px; height:32px; padding:0 9px; border:none; border-radius:9px; background:#f5f5f8; color:#e08a2b; font-size:12px; font-weight:600; cursor:pointer;">${ICON.fire}<span data-ref="fireN" style="color:#5C5C5C; font-variant-numeric:tabular-nums;">0</span></button>
        <button data-ref="star" style="display:flex; align-items:center; gap:4px; height:32px; padding:0 9px; border:none; border-radius:9px; background:#f5f5f8; color:#d9a400; font-size:12px; font-weight:600; cursor:pointer;">${ICON.star}<span data-ref="starN" style="color:#5C5C5C; font-variant-numeric:tabular-nums;">0</span></button>
        <button data-ref="comments" style="display:flex; align-items:center; gap:4px; height:32px; padding:0 9px; border:none; border-radius:9px; background:#f5f5f8; color:${PURPLE}; font-size:12px; font-weight:600; cursor:pointer;">${ICON.chat}<span data-ref="commentN" style="color:#5C5C5C; font-variant-numeric:tabular-nums;">0</span></button>
      </div>
    </div>
  </div>
</div>

<div data-ref="expandWrap" style="display:none; position:absolute; z-index:7; left:50%; transform:translateX(-50%); bottom:max(16px, env(safe-area-inset-bottom)); pointer-events:auto;">
  <button data-ref="expand" title="развернуть плеер" style="display:flex; align-items:center; gap:8px; height:40px; padding:0 16px 0 13px; border:none; border-radius:999px; background:#fff; color:${PURPLE}; font-size:12.5px; font-weight:700; cursor:pointer; box-shadow:0 6px 20px rgba(38,38,40,0.18);">
    <svg viewBox="0 0 24 24" width="17" height="17"><path d="M6 14l6-6 6 6" fill="none" stroke="${PURPLE}" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"></path></svg>
    <span data-ref="expandLabel">плеер</span>
  </button>
</div>

<div data-ref="listWrap" style="display:none; position:absolute; z-index:6; inset:0; overflow-y:auto; background:linear-gradient(180deg,#eef1f7 0%,#e4e7ef 100%); pointer-events:auto;">
  <div style="max-width:720px; margin:0 auto; padding:118px 18px 168px;">
    <div style="display:flex; flex-wrap:wrap; gap:24px; align-items:flex-end;">
      <img data-ref="listCover" alt="" style="width:188px; height:188px; flex:none; border-radius:16px; object-fit:cover; background:#EBEBEB; box-shadow:0 18px 44px rgba(38,38,40,0.22);">
      <div style="flex:1; min-width:280px;">
        <div data-ref="listTitle" style="font-family:var(--font-display); font-weight:900; font-size:32px; line-height:1.05; letter-spacing:-0.02em; color:#262626;"></div>
        <div data-ref="listArtist" style="margin-top:4px; font-size:16px; color:${MUTE};"></div>
        <div data-ref="listArtistSocial" style="display:flex; align-items:center; gap:8px; margin-top:12px;"></div>
      </div>
    </div>
    <div style="margin-top:28px;">
      <div data-ref="listLabel" style="font-size:11px; letter-spacing:0.16em; text-transform:uppercase; font-weight:700; color:${MUTE}; padding:0 6px 10px;"></div>
      <div data-ref="listRows"></div>
    </div>
  </div>
</div>

<div data-ref="albumWrap" style="display:none; position:absolute; z-index:8; inset:0; overflow-y:auto; background:linear-gradient(180deg,#f3f5fa 0%,#e6e9f2 100%); pointer-events:auto;">
  <button data-ref="albumClose" style="position:absolute; top:18px; right:18px; width:40px; height:40px; border:none; border-radius:50%; background:#fff; color:#262626; cursor:pointer; box-shadow:0 2px 10px rgba(38,38,40,0.12); font-size:18px; line-height:1;">✕</button>
  <div style="max-width:560px; margin:0 auto; padding:84px 24px 48px; display:flex; flex-direction:column; align-items:center; text-align:center;">
    <img data-ref="albumCover" alt="" style="width:min(72vw,360px); height:min(72vw,360px); border-radius:20px; object-fit:cover; background:#EBEBEB; box-shadow:0 24px 60px rgba(38,38,40,0.28);">
    <div data-ref="albumTag" style="margin-top:22px; font-size:10px; letter-spacing:0.16em; text-transform:uppercase; font-weight:700; color:${PURPLE};"></div>
    <div data-ref="albumTitle" style="margin-top:8px; font-family:var(--font-display); font-weight:900; font-size:34px; line-height:1.05; letter-spacing:-0.02em; color:#262626;"></div>
    <div data-ref="albumArtist" style="margin-top:4px; font-size:17px; color:${MUTE};"></div>
    <div data-ref="albumAlbum" style="margin-top:2px; font-size:13px; color:#a4a8b8;"></div>
    <div data-ref="albumArtistSocial" style="display:flex; align-items:center; justify-content:center; gap:10px; margin-top:16px;"></div>
    <button data-ref="albumPlay" style="margin-top:24px; display:inline-flex; align-items:center; gap:8px; height:48px; padding:0 24px; border:none; border-radius:999px; background:${PURPLE}; color:#fff; font-size:15px; font-weight:700; cursor:pointer; box-shadow:0 10px 24px rgba(104,90,233,0.35);">▶ <span data-ref="albumPlayLabel"></span></button>
    <div style="margin-top:28px; width:100%;">
      <div data-ref="albumLinksLabel" style="font-size:11px; letter-spacing:0.14em; text-transform:uppercase; font-weight:700; color:#a4a8b8; text-align:left;"></div>
      <div data-ref="albumLinks" style="display:flex; flex-direction:column; gap:10px; margin-top:12px;"></div>
    </div>
  </div>
</div>`;
  }
}

function esc(s: string): string {
  return s.replace(/[<>&]/g, (m) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[m] ?? m);
}
